<script setup lang="ts">
import { h, ref, watch, computed } from 'vue'
import type { VNode } from 'vue'
import QRCode from 'qrcode'

interface QrCodeProps {
  value: string
  foreground?: string
  background?: string
  image?: string | null
  imageSize?: number
  borderRatio?: number
}

const props = withDefaults(defineProps<QrCodeProps>(), {
  foreground: '#000',
  background: '#fff',
  image: null,
  imageSize: 64,
  borderRatio: 0.02,
})

const svgVNode = ref<VNode | null>(null)

const borderWidth = computed(() => 256 * props.borderRatio)
const centerImageX = computed(() => (256 - props.imageSize) / 2)
const centerImageY = computed(() => (256 - props.imageSize) / 2)

const parseSvgStringToVNode = (svgString: string): VNode => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(svgString, 'image/svg+xml')
  const svgEl = doc.documentElement
  const convertNode = (node: Element): VNode => {
    const tag = node.tagName
    const attrs: Record<string, string> = {}
    for (const attr of Array.from(node.attributes)) {
      attrs[attr.name] = attr.value
    }
    const children = Array.from(node.childNodes)
      .map(child => {
        if (child.nodeType === 1) return convertNode(child as Element)
        if (child.nodeType === 3) return child.textContent
        return null
      })
      .filter(Boolean)
    return h(tag, attrs, children)
  }
  return convertNode(svgEl)
}

const generateQr = async () => {
  const svgString = await QRCode.toString(props.value, {
    type: 'svg',
    color: {
      dark: props.foreground,
      light: props.background,
    },
    width: 256,
    margin: 0,
  })
  svgVNode.value = parseSvgStringToVNode(svgString)
}

watch(
  () => [props.value, props.foreground, props.background],
  () => {
    generateQr()
  },
  { immediate: true }
)
</script>

<template>
  <div class="qr-container">
    <component :is="svgVNode" v-if="svgVNode" class="qr-svg" />
    <template v-if="image">
      <foreignObject
        :x="centerImageX - borderWidth"
        :y="centerImageY - borderWidth"
        :width="imageSize + borderWidth * 2"
        :height="imageSize + borderWidth * 2"
        class="qr-image-fo"
      >
        <div
          class="qr-image-border"
          :style="{
            width: imageSize + borderWidth * 2 + 'px',
            height: imageSize + borderWidth * 2 + 'px',
            background: background,
          }"
        >
          <img
            :src="image"
            :width="imageSize"
            :height="imageSize"
            class="qr-image"
            alt="center image"
            draggable="false"
          />
        </div>
      </foreignObject>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.qr-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
}

.qr-image-fo {
  pointer-events: none;
  position: absolute;
}

.qr-image-border {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.qr-image {
  border-radius: 6px;
  display: block;
  object-fit: contain;
}
</style>
